import React, { useEffect, ReactNode, useRef } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useGeneralStore } from 'src/store';
import {
  getGoogleTrackingKey,
  hasGoogleTracking,
  pushGooglePageUpdate,
} from 'src/hooks/useBranding';
// import { useQuery } from 'src/utils';
import { useAuthInitialization } from 'src/hooks/useAuthInitialization';
import { W2RootGate } from '@features/w2/W2RootGate';
import { W2HostGate } from '@features/w2/host/W2HostGate';
import { W2ContractPage } from '@features/w2/contract/W2ContractPage';
import {
  W2FlowLayout,
  W2IndexRedirect,
  W2FlowCatchAll,
  W2RegisterRoute,
  W2LoginRoute,
  W2ForgotRoute,
  W2ResetRoute,
  W2PhqRoute,
  W2DoneRoute,
} from '@features/w2/flow';
import { W2LinkError } from '@features/w2/components/W2LinkError';
import { SelectionFlowPage } from '@features/w2/selection/SelectionFlowPage';
import LandingPage from '@features/landing/LandingPage';
import OurPlansPage from '@features/landing/OurPlansPage';
import { PublicLayout } from 'src/layouts/PublicLayout';
import { PrivateLayout } from 'src/layouts/PrivateLayout';
import { ProfileLayout } from 'src/layouts/ProfileLayout';
import {
  HomePage,
  ProfilePage as ProfileInnerPage,
  YourPlansPage,
  BillingPage,
  FAQsPage,
  ResourcesPage,
  IDCards,
  IDCardView,
} from 'src/features/profile/pages';

import LoginPage from '@pages/LoginPage';
import ForgotPasswordPage from '@pages/ForgotPasswordPage';
import ResetPasswordPage from '@pages/ResetPasswordPage';
import RegistrationPage from '@pages/RegistrationPage';
import OnBoarding from '@pages/OnBoarding';
import ThankYou from '@pages/ThankYou';
import { TesterPage } from '@pages/TesterPage';
// import { TesterPagePayment } from '@pages/TesterPagePayment';
import NotFoundPage from '@pages/NotFoundPage';
import ProfilePage from '@pages/Profile';

import SessionExpiredModal from '@components/shared/session-expired-modal/SessionExpiredModal';
import { AnimatedDots } from '@components/shared/animated-dots/AnimatedDots';

const queryClient = new QueryClient();

interface BrandInitializerProps {
  children: ReactNode;
}

const BrandInitializer: React.FC<BrandInitializerProps> = ({ children }) => {
  return <>{children}</>;
};

interface AuthInitializerProps {
  children: ReactNode;
}

const AuthInitializer: React.FC<AuthInitializerProps> = ({ children }) => {
  useAuthInitialization();
  return <>{children}</>;
};
const RootRoute: React.FC = () => {
  const isLoadingConfig = useGeneralStore((s) => s.isLoadingConfig);
  const landing = useGeneralStore((s) => s.siteConfig?.landing);

  if (isLoadingConfig) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#F6F1E9]">
        <AnimatedDots />
      </div>
    );
  }

  return landing?.enabled ? <LandingPage /> : <Navigate to="/form" replace />;
};

export const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [pathname]);

  return null;
};

const GoogleTrackingPageUpdates: React.FC = () => {
  const location = useLocation();
  const tracking = useGeneralStore((s) => s.siteConfig?.tracking);
  const pageKey = `${location.pathname}${location.search}${location.hash}`;
  const trackingKey = getGoogleTrackingKey(tracking);
  const previousPageKeyRef = useRef<string | null>(null);
  const previousTrackingKeyRef = useRef<string>('');

  useEffect(() => {
    if (!hasGoogleTracking(tracking)) {
      previousPageKeyRef.current = pageKey;
      previousTrackingKeyRef.current = trackingKey;
      return;
    }

    const isInitialTrackingRun =
      previousPageKeyRef.current === null ||
      previousTrackingKeyRef.current !== trackingKey;

    if (isInitialTrackingRun) {
      previousPageKeyRef.current = pageKey;
      previousTrackingKeyRef.current = trackingKey;
      return;
    }

    if (previousPageKeyRef.current !== pageKey) {
      if (location.pathname !== '/form') {
        pushGooglePageUpdate(tracking, { pageType: 'route' });
      }
      previousPageKeyRef.current = pageKey;
    }

    previousTrackingKeyRef.current = trackingKey;
  }, [pageKey, tracking, trackingKey]);

  return null;
};

const AppRouter: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <ScrollToTop />
        <GoogleTrackingPageUpdates />
        <AuthInitializer>
          <BrandInitializer>
            <SessionExpiredModal />
            {/* W2RootGate: legacy `/?t=<token>` entry → redirect to `/e/<token>`
                (D3 shim, one release cycle; ADR-0010 trail preserved in the
                component). */}
            <W2RootGate>
              {/* W2HostGate: on a W2 group host (webClientType SmallEmployerTenant)
                  block every route outside the enrollment-link allowlist with a
                  branded page. Runs INSIDE W2RootGate so the legacy `/?t=<token>`
                  redirect fires first; inert on B2C hosts and while resolving. */}
              <W2HostGate>
              <Routes>
                <Route path="/" element={<RootRoute />} />
                {/* Public tokenized contract-signing page (ticket 06.5, path-token
                  per D1): the employer-prospect counterpart of the W2 `/e/<token>`
                  employee entry — standalone hosted page, outside every app layout,
                  no chrome. The bare `/contract` route keeps the legacy `?t=` read
                  (and the missing-token → invalid policy pinned by e2e). */}
                <Route path="/contract" element={<W2ContractPage />} />
                <Route
                  path="/contract/:contractToken"
                  element={<W2ContractPage />}
                />

                {/* W2 employee enrollment flow (`/e/<employerToken>/…`, master
                  plan C-1): W2FlowLayout resolves + activates BEFORE any child
                  renders (C-7); children are thin route screens over the reused
                  branded B2C components. `/e` with no token = the truncated-link
                  error (same policy as the old empty `/?t=`). */}
                <Route path="/e">
                  <Route index element={<W2LinkError status={null} />} />
                  <Route path=":employerToken" element={<W2FlowLayout />}>
                    <Route index element={<W2IndexRedirect />} />
                    <Route path="register" element={<W2RegisterRoute />} />
                    <Route path="login" element={<W2LoginRoute />} />
                    <Route path="forgot" element={<W2ForgotRoute />} />
                    <Route path="reset" element={<W2ResetRoute />} />
                    <Route path="phq" element={<W2PhqRoute />} />
                    <Route path="done" element={<W2DoneRoute />} />
                    <Route path="*" element={<W2FlowCatchAll />} />
                  </Route>
                </Route>
                {/* W2 employee plan self-selection (WP6): a returning,
                  normally-authenticated surface — NOT under W2RootGate (that
                  gate is token-only). The page guards on the JWT itself and
                  bounces to `/login?next=/select-plans` when unauthenticated. */}
                <Route path="/select-plans" element={<SelectionFlowPage />} />
                <Route path="/landing/plans" element={<OurPlansPage />} />

                {/* Публичная часть */}
                <Route element={<PublicLayout />}>
                  <Route path="/login" element={<LoginPage />} />
                  <Route
                    path="/forgot-password"
                    element={<ForgotPasswordPage />}
                  />
                  <Route
                    path="/reset-password"
                    element={<ResetPasswordPage />}
                  />
                  <Route path="/registration" element={<RegistrationPage />} />
                </Route>

                {/* Приватная часть */}
                <Route element={<PrivateLayout />}>
                  <Route path="/form" element={<OnBoarding />} />
                  <Route path="/thanks" element={<ThankYou />} />
                  <Route path="/tester" element={<TesterPage />} />
                  {/* <Route path="/payment" element={<TesterPagePayment />} /> */}
                </Route>

                {/* Профиль с отдельным лейаутом */}
                <Route element={<ProfileLayout />}>
                  <Route path="/healthhub" element={<ProfilePage />}>
                    <Route index element={<HomePage />} />
                    <Route path="profile" element={<ProfileInnerPage />} />
                    <Route path="your-plans" element={<YourPlansPage />} />
                    <Route path="billing" element={<BillingPage />} />
                    <Route path="faqs" element={<FAQsPage />} />
                    <Route path="resources" element={<ResourcesPage />} />
                    <Route path="id-cards" element={<IDCards />} />
                    <Route
                      path="id-cards/:subscriptionId/:applicantId"
                      element={<IDCardView />}
                    />
                  </Route>
                </Route>

                {/* 404 fallback */}
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
              </W2HostGate>
            </W2RootGate>
          </BrandInitializer>
        </AuthInitializer>
      </Router>
    </QueryClientProvider>
  );
};

export default AppRouter;
